import moment from 'moment-timezone';
import sortBy from 'lodash/sortBy';
import has from 'lodash/has';
import { prepareModelTimeForForm } from './modelTime';
import config from '../../../config';
import { USE_SERVICE_DURATION_FOR_TIMES } from '../../main/barbers/constants';
import { getDateFormat, getTimezone } from '../../auth/selectors';
import { store } from '../../../main';
import * as reportRangePresets from '../DatePickerV2/presets';

export function getStartOfDay(timezone = null, day = null) {
  if (!timezone) {
    return moment().startOf('day');
  }
  const date = day || moment.tz(timezone).format(config.dateFormatISO);
  return moment.tz(date, config.dateFormatISO, timezone).startOf('day');
}

export function getUTCStartOfDay({ timezone, day }) {
  const date = day || moment.tz(timezone).startOf('day').utc().format(config.dateFormatISO);
  return moment.utc(date, config.dateFormatISO).startOf('day');
}

export function formatTime(h, m) {
  return moment().hours(h).minutes(m).seconds(0).format(config.timeFormat);
}

export function formatTimeFromDate(date, timezone) {
  return moment.tz(date, timezone).format(config.timeFormat);
}

export function formatDateTime(dateTime, timezone) {
  return moment.tz(dateTime, timezone).format(config.dateTimeFormat);
}

export function formatDate(dateTime, timezone, format = config.dateFormat) {
  return moment.tz(dateTime, timezone).format(format);
}

export function dateToISO(date) {
  return moment(date, config.dateFormatRFC2822).format(config.dateFormatISO);
}

export function prepareTimes(hours) {
  return {
    startTime: formatTime(hours.start.hours, hours.start.minutes),
    endTime: formatTime(hours.end.hours, hours.end.minutes),
  };
}

export function periodToTime(start, end) {
  return {
    start: start.format('HH:mm'),
    end: end.format('HH:mm'),
  };
}

export function hoursToPeriod(hours, day = null, timezone = null) {
  if (has(hours, 'startHour')) {
    return {
      start: getStartOfDay(timezone, day).hours(hours.startHour).minutes(hours.startMinute),
      end: getStartOfDay(timezone, day).hours(hours.finishHour).minutes(hours.finishMinute),
    };
  }

  if (has(hours, 'start')) {
    const parseTime = (time, format) => moment(time, 'H:m').format(format);

    if (has(hours.start, 'hours')) {
      return {
        start: getStartOfDay(timezone, day).hours(hours.start.hours).minutes(hours.start.minutes),
        end: getStartOfDay(timezone, day).hours(hours.end.hours).minutes(hours.end.minutes),
      };
    }
    return {
      start: getStartOfDay(timezone, day).hours(parseTime(hours.start, 'H')).minutes(parseTime(hours.start, 'm')),
      end: getStartOfDay(timezone, day).hours(parseTime(hours.end, 'H')).minutes(parseTime(hours.end, 'm')),
    };
  }
}

// We did not use this function yet
// but it ready for addition hours to the current day schedule
export function addHours(scheduleHours, dayOnHours = null) {
  let hours = [];
  const schedule = hoursToPeriod(scheduleHours);

  if (!dayOnHours) {
    hours.push(periodToTime(schedule.start, schedule.end));
    return hours;
  }

  const dayOn = hoursToPeriod(dayOnHours);

  if (schedule.end.isBefore(dayOn.start) || schedule.start.isAfter(dayOn.end)) {
    hours.push(periodToTime(schedule.start, schedule.end));
    hours.push(periodToTime(dayOn.start, dayOn.end));
  } else {
    const start = moment.min(schedule.start, dayOn.start);
    const end = moment.max(schedule.end, dayOn.end);
    hours.push(periodToTime(start, end));
  }
  hours = sortBy(hours, (time => time.start));

  return hours;
}

export function expandPeriod(fromPeriod, toPeriod) {
  const period = { ...fromPeriod };

  if (period.start.isAfter(toPeriod.start)) {
    period.start = toPeriod.start;
  }
  if (period.end.isBefore(toPeriod.end)) {
    period.end = toPeriod.end;
  }
  return period;
}

export function momentToHoursMinutes(momentObject) {
  /*
    moment object has 'days' value, so 24 hours are counted as one day,
    therefore 24 hours are saved as 1 day and 0 hours
  */
  return {
    hours: (momentObject.days() * 24) + momentObject.hours(),
    minutes: momentObject.minutes(),
  };
}

export function minutesToHoursMinutes(minutes) {
  const duration = moment.duration(minutes, 'minutes');
  return {
    hours: (duration.days() * 24) + duration.hours(),
    minutes: duration.minutes(),
  };
}

export function minsToDaysHoursMins(mins) {
  const duration = moment.duration(mins, 'minutes');
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();

  let daysHoursMins = '';
  if (days) {
    daysHoursMins += `${days} day`;
    if (days > 1) {
      daysHoursMins += 's';
    }
    daysHoursMins += ' ';
  }
  if (hours) {
    daysHoursMins += `${hours} hour`;
    if (hours > 1) {
      daysHoursMins += 's';
    }
    daysHoursMins += ' ';
  }
  if (minutes) {
    daysHoursMins += `${minutes} min`;
    if (minutes > 1) {
      daysHoursMins += 's';
    }
    daysHoursMins += ' ';
  }

  return daysHoursMins;
}

export const parseMultiFieldValues = (barber) => {
  const data = { ...barber };
  prepareModelTimeForForm(data, 'advanceBookMins');
  prepareModelTimeForForm(data, 'advanceCancelMins');
  prepareModelTimeForForm(data, 'customerReminder.barber_reminder.time');
  prepareModelTimeForForm(data, 'customerReminder.reminder.time');

  return data;
};

export const parseCustomerBookingInterval = barber =>
  (barber.useServiceDurationForTimes ? USE_SERVICE_DURATION_FOR_TIMES : barber.minServiceLen);

// '1:00 pm' => '13:00'
export const regularToMilitaryTime = time => moment(time.toLowerCase(), 'hh:mm a').format('HH:mm');

// '13:00' => '1:00 pm'
export const militaryToRegularTime = time => moment(time, 'HH:mm').format('hh:mm a');

export const formatDateForShop = (date, format) => {
  const dateFormat = getDateFormat(store.getState());
  const timezone = getTimezone(store.getState());
  return moment.tz(date, timezone).format(format || dateFormat);
};

export const getDefaultReportRange = () => reportRangePresets.userLocalDatePresets.getRange('Today');
